import { Injectable } from '@angular/core';
import { CollectionActionCommand, CaseHistoryEntryDto, CaseInteractionDto } from '../models/collection-case.model';
import { TimelineEvent } from '../../ui/ui.types';

export interface MockCollectionAction {
  readonly id: string;
  readonly caseId: string;
  readonly debtorId?: string;
  readonly type: string;
  readonly channel: string;
  readonly plannedDate: string;
  readonly executionDate?: string;
  readonly status: 'PLANNED' | 'DONE' | 'FAILED';
  readonly result?: string;
  readonly comment?: string;
  readonly createdAt: string;
  readonly createdBy: string;
}

/** Session-scoped source of truth for collection actions in mock mode. */
@Injectable({ providedIn: 'root' })
export class ActionMockStateService {
  private readonly actions: MockCollectionAction[] = [];

  add(command: CollectionActionCommand, context?: { debtorId?: string; createdBy?: string }): MockCollectionAction {
    const now = new Date().toISOString();
    const plannedDate = command.scheduledDate || now.slice(0, 10);
    const isFuture = plannedDate > now.slice(0, 10);
    const action: MockCollectionAction = {
      id: `mock-action-${Date.now()}-${this.actions.length}`,
      caseId: command.caseId,
      debtorId: context?.debtorId,
      type: command.actionType,
      channel: command.channel,
      plannedDate,
      executionDate: isFuture ? undefined : now,
      status: isFuture ? 'PLANNED' : (command.outcome === 'NO_ANSWER' || command.outcome === 'WRONG_NUMBER' ? 'FAILED' : 'DONE'),
      result: command.outcome,
      comment: command.report,
      createdAt: now,
      createdBy: context?.createdBy ?? 'Agent démonstration',
    };
    this.actions.unshift(action);
    return action;
  }

  getForCase(caseId: string): MockCollectionAction[] {
    return this.actions.filter(a => a.caseId === caseId);
  }

  getForDebtor(debtorId: string, caseIds: string[] = []): MockCollectionAction[] {
    return this.actions.filter(a => a.debtorId === debtorId || caseIds.includes(a.caseId));
  }

  getLastForCase(caseId: string): MockCollectionAction | undefined {
    return this.getForCase(caseId).slice().sort((a, b) =>
      (b.executionDate ?? b.createdAt).localeCompare(a.executionDate ?? a.createdAt),
    )[0];
  }

  toTimeline(action: MockCollectionAction): TimelineEvent {
    const channel = (action.channel || 'telephone') as TimelineEvent['channel'];
    return {
      id: action.id,
      date: new Date(action.executionDate ?? action.plannedDate + 'T00:00:00'),
      channel,
      type: 'action',
      description: `${this.label(action.type)}${action.status === 'PLANNED' ? ' — planifiée' : ''}${action.result ? ` — ${action.result}` : ''}${action.comment ? `. ${action.comment}` : ''}`,
      agent: action.createdBy,
    };
  }

  toInteraction(action: MockCollectionAction): CaseInteractionDto {
    return {
      interactionId: action.id,
      channel: action.type === 'PHONE_CALL' ? 'PHONE' : action.type,
      actionLabel: this.label(action.type),
      interactionDate: action.executionDate ?? action.plannedDate,
      agentName: action.createdBy,
      outcome: action.result ?? action.status,
      hasPromise: false,
    };
  }

  toHistory(action: MockCollectionAction): CaseHistoryEntryDto {
    return {
      entryId: action.id,
      entryDate: action.executionDate ?? action.createdAt,
      actor: action.createdBy,
      action: action.status === 'PLANNED' ? 'Action planifiée' : 'Action clôturée',
      details: `${this.label(action.type)}${action.result ? ` — ${action.result}` : ''}${action.comment ? `. ${action.comment}` : ''}`,
    };
  }

  private label(type: string): string {
    return ({ PHONE_CALL: 'Appel téléphonique', SMS: 'SMS', EMAIL: 'E-mail', LETTER: 'Courrier', VISIT: 'Visite', RELANCE: 'Relance', CONTACT_SEARCH: 'Recherche contact' } as Record<string, string>)[type] ?? type;
  }
}
